import React, { useState, useEffect } from "react";
import {
  Box,
  Card,
  CardContent,
  Typography,
  Avatar,
  Button,
  Divider,
  TextField,
  Paper,
  alpha,
  useTheme,
  Snackbar,
  Alert,
  Chip,
  useMediaQuery,
  BottomNavigation,
  BottomNavigationAction,
  IconButton,
  CircularProgress,
  Tooltip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  RadioGroup,
  FormControlLabel,
  Radio,
} from "@mui/material";
import LogoutIcon          from "@mui/icons-material/Logout";
import PersonIcon          from "@mui/icons-material/Person";
import EmailIcon           from "@mui/icons-material/Email";
import PhoneIcon           from "@mui/icons-material/Phone";
import LocationOnIcon      from "@mui/icons-material/LocationOn";
import ArticleIcon         from "@mui/icons-material/Article";
import SettingsIcon        from "@mui/icons-material/Settings";
import SaveIcon            from "@mui/icons-material/Save";
import DeleteIcon          from "@mui/icons-material/Delete";
import DownloadIcon        from "@mui/icons-material/Download";
import EditIcon            from "@mui/icons-material/Edit";
import CloseIcon           from "@mui/icons-material/Close";
import HistoryIcon         from "@mui/icons-material/History";
import PictureAsPdfIcon    from "@mui/icons-material/PictureAsPdf";
import ImageIcon           from "@mui/icons-material/Image";
import AddIcon             from "@mui/icons-material/Add";
import OpenInNewIcon       from "@mui/icons-material/OpenInNew";
import { useNavigate }     from "react-router-dom";
import ArrowBackIcon       from "@mui/icons-material/ArrowBack";
import HomeIcon            from "@mui/icons-material/Home";
import AccountCircleIcon   from "@mui/icons-material/AccountCircle";
import DescriptionIcon     from "@mui/icons-material/Description";
import WarningAmberIcon    from "@mui/icons-material/WarningAmber";

// ─── Template thumbnail helper ────────────────────────────────────────────────
const getTemplateThumbnail = (templateId) => `/template-thumbnails/template${templateId}.png`;

// ─── Format metadata ──────────────────────────────────────────────────────────
const FORMAT_META = {
  pdf:  { label: "PDF",  bg: "#fde8e8", color: "#c62828" },
  png:  { label: "PNG",  bg: "#e3f0ff", color: "#1565c0" },
  doc:  { label: "DOC",  bg: "#e8f5e9", color: "#2e7d32" },
  docx: { label: "DOCX", bg: "#e8f5e9", color: "#2e7d32" },
};

// ─── Design tokens ────────────────────────────────────────────────────────────
const BLUE = {
  primary:   "#002B5B",
  primaryDk: "#001a36",
  light:     "#EFF7FF",
  mid:       "#0288D1",
  accent:    "#0288D1",
  navBg:     "#002B5B",
  navHover:  "rgba(2,136,209,0.15)",
  navActive: "rgba(2,136,209,0.22)",
  border:    "#B3D9F7",
  text:      "#0D4C8B",
};

// ─── API base ─────────────────────────────────────────────────────────────────
const API = "https://resume-maker-lc.onrender.com/api";
const authHeader = () => ({ Authorization: `Bearer ${localStorage.getItem("token")}` });

// ═════════════════════════════════════════════════════════════════════════════
const MyAccountPage = () => {
  const [tabValue, setTabValue]       = useState(0);
  const [user, setUser]               = useState(null);
  const [isEditing, setIsEditing]     = useState(false);
  const [editedUser, setEditedUser]   = useState({});
  const [loading, setLoading]         = useState(true);
  const [deleteAccConfirm, setDeleteAccConfirm] = useState(false);
  const [snackbar, setSnackbar]       = useState({ open: false, message: "", severity: "success" });

  // Resume history
  const [resumeHistory, setResumeHistory]     = useState([]);
  const [historyLoading, setHistoryLoading]   = useState(false);
  const [deletingId, setDeletingId]           = useState(null);
  const [historyDeleteConfirm, setHistoryDeleteConfirm] = useState(null);

  // Download dialog
  const [downloadRecord, setDownloadRecord]   = useState(null);
  const [downloadFormat, setDownloadFormat]   = useState("pdf");

  const theme    = useTheme();
  const navigate = useNavigate();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));

  // ─── Utility ───────────────────────────────────────────────────────────────
  const notify = (message, severity = "success") =>
    setSnackbar({ open: true, message, severity });

  const getInitials    = (name) => (name ? name.charAt(0).toUpperCase() : "?");
  const getAvatarColor = (name = "") => {
    const palette = ["#002B5B","#0288D1","#45B7D1","#7c3aed","#0891b2","#059669","#d97706","#dc2626"];
    let h = 0;
    for (let i = 0; i < name.length; i++) h = name.charCodeAt(i) + ((h << 5) - h);
    return palette[Math.abs(h) % palette.length];
  };

  const formatFileSize = (bytes) => {
    if (!bytes) return "—";
    if (bytes < 1024)        return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
    return `${(bytes / (1024 * 1024)).toFixed(2)} MB`;
  };

  const formatDate = (s) => {
    if (!s) return "—";
    const d = new Date(s);
    return (
      d.toLocaleDateString("en-IN", { day: "2-digit", month: "short", year: "numeric" }) +
      "  " +
      d.toLocaleTimeString("en-IN", { hour: "2-digit", minute: "2-digit" })
    );
  };

  const getFormatIcon = (fmt) => {
    switch (fmt?.toLowerCase()) {
      case "pdf":  return <PictureAsPdfIcon fontSize="small" sx={{ color: "#d32f2f" }} />;
      case "png":  return <ImageIcon        fontSize="small" sx={{ color: "#0288D1" }} />;
      default:     return <ArticleIcon      fontSize="small" sx={{ color: "#2e7d32" }} />;
    }
  };

  // ─── Auth / user fetch ──────────────────────────────────────────────────────
  useEffect(() => {
    (async () => {
      const token = localStorage.getItem("token");
      if (!token) { navigate("/signup"); return; }
      try {
        const res  = await fetch(`${API}/auth/me`, { headers: authHeader() });
        const data = await res.json();
        if (res.ok) { setUser(data); setEditedUser(data); }
        else navigate("/mainpage");
      } catch { navigate("/mainpage"); }
      finally { setLoading(false); }
    })();
  }, [navigate]);

  useEffect(() => { if (tabValue === 1) fetchResumeHistory(); }, [tabValue]);

  // ─── Resume History ─────────────────────────────────────────────────────────
  const fetchResumeHistory = async () => {
    setHistoryLoading(true);
    try {
      const res  = await fetch(`${API}/resumes/history`, { headers: authHeader() });
      const data = await res.json();
      setResumeHistory(res.ok && Array.isArray(data) ? data : []);
    } catch { setResumeHistory([]); }
    finally { setHistoryLoading(false); }
  };

  const handleDeleteHistory = async (id) => {
    setDeletingId(id);
    try {
      const res = await fetch(`${API}/resumes/${id}`, {
        method: "DELETE", headers: authHeader(),
      });
      if (res.ok) {
        setResumeHistory((p) => p.filter((r) => r._id !== id));
        notify("Resume record deleted successfully.");
      } else notify("Failed to delete. Try again.", "error");
    } catch { notify("Error deleting record.", "error"); }
    finally { setDeletingId(null); setHistoryDeleteConfirm(null); }
  };

  // ─── Download dialog ────────────────────────────────────────────────────────
  const openDownload = (record) => {
    setDownloadRecord(record);
    setDownloadFormat(record.fileFormat || "pdf");
  };

  const handleReDownload = () => {
    if (!downloadRecord) return;
    setDownloadRecord(null);
    navigate("/mainpage", {
      state: {
        selectedTemplate:   downloadRecord.templateId,
        selectedTheme:      downloadRecord.themeColor || "#0951b0ff",
        autoDownload:       true,
        autoDownloadFormat: downloadFormat,
        autoDownloadName:   downloadRecord.fileName,
      },
    });
  };

  const handleEditTemplate = (record) =>
    navigate("/mainpage", {
      state: { selectedTemplate: record.templateId, selectedTheme: record.themeColor || "#0951b0ff" },
    });

  // ─── Profile helpers ────────────────────────────────────────────────────────
  const handleLogout     = () => { localStorage.removeItem("token"); navigate("/signup"); };
  const handleEditToggle = () => { setIsEditing(!isEditing); if (isEditing) setEditedUser(user); };
  const handleInputChange = (field, value) =>
    setEditedUser((p) => ({ ...p, [field]: value }));

  const handleSaveProfile = async () => {
    if (editedUser.phone && editedUser.phone.toString().replace(/\D/g, "").length !== 10) {
      notify("Please enter a valid 10-digit phone number.", "error"); return;
    }
    try {
      const res = await fetch(`${API}/auth/update-profile`, {
        method:  "PUT",
        headers: { "Content-Type": "application/json", ...authHeader() },
        body:    JSON.stringify(editedUser),
      });
      if (res.ok) {
        const updated = await res.json();
        setUser(updated); setEditedUser(updated);
        setIsEditing(false);
        notify("Profile updated successfully!");
      } else notify("Failed to update profile.", "error");
    } catch { notify("Error updating profile.", "error"); }
  };

  const handleDeleteAccount = async () => {
    try {
      const res = await fetch(`${API}/auth/delete-account`, {
        method: "DELETE", headers: authHeader(),
      });
      if (res.ok) {
        localStorage.removeItem("token");
        notify("Account deleted successfully.");
        setTimeout(() => navigate("/signup"), 1500);
      } else notify("Failed to delete account.", "error");
    } catch { notify("Error deleting account.", "error"); }
    finally { setDeleteAccConfirm(false); }
  };

  // ─── Loading / Error ────────────────────────────────────────────────────────
  if (loading) {
    return (
      <Box sx={{ display:"flex", justifyContent:"center", alignItems:"center", minHeight:"100vh", bgcolor: BLUE.light }}>
        <Box sx={{ textAlign:"center" }}>
          <CircularProgress sx={{ color: BLUE.primary, mb:2 }} size={48} />
          <Typography color="text.secondary">Loading your profile…</Typography>
        </Box>
      </Box>
    );
  }
  if (!user) {
    return (
      <Box sx={{ display:"flex", flexDirection:"column", justifyContent:"center", alignItems:"center", minHeight:"100vh", gap:3 }}>
        <Typography variant="h6" color="text.secondary">Failed to load user data</Typography>
        <Button variant="contained" onClick={() => navigate("/mainpage")} sx={{ borderRadius:3, bgcolor: BLUE.primary }}>
          Go to Main Page
        </Button>
      </Box>
    );
  }

  // ─── InfoField ──────────────────────────────────────────────────────────────
  const InfoField = ({ icon, label, value, field }) => {
    const [local, setLocal] = useState(editedUser[field] || "");
    useEffect(() => { setLocal(editedUser[field] || ""); }, [field]);

    const commit = () => { if ((editedUser[field] || "") !== local) handleInputChange(field, local); };

    return (
      <Box sx={{
        display:"flex", alignItems:"center",
        mb:2, px:2.5, py:2,
        borderRadius:2,
        bgcolor: isEditing ? BLUE.light : "#f8faff",
        border: isEditing ? `1.5px solid ${BLUE.mid}` : `1.5px solid ${BLUE.border}`,
        transition:"all 0.2s",
      }}>
        <Box sx={{ color: BLUE.primary, mr:2, flexShrink:0, display:"flex" }}>{icon}</Box>
        <Box sx={{ flex:1, minWidth:0 }}>
          <Typography variant="caption" sx={{ color: BLUE.primary, fontWeight:700, fontSize:"0.72rem", letterSpacing:0.5, textTransform:"uppercase", display:"block", mb:0.5 }}>
            {label}
          </Typography>
          {isEditing ? (
            <TextField
              fullWidth size="small" value={local} variant="standard"
              onChange={(e) => {
                const v = field === "phone" ? e.target.value.replace(/\D/g,"").slice(0,10) : e.target.value;
                setLocal(v);
              }}
              onBlur={commit}
              onKeyDown={(e) => { if (e.key === "Enter") { commit(); e.target.blur(); } }}
              inputProps={{ maxLength: field==="phone" ? 10 : undefined, inputMode: field==="phone" ? "numeric" : undefined }}
              sx={{
                "& .MuiInput-underline:before": { borderBottomColor: BLUE.mid },
                "& .MuiInput-underline:after":  { borderBottomColor: BLUE.primary },
                "& input": { fontWeight:500, color:"#0f172a", fontSize: isMobile ? "0.875rem" : "1rem" },
              }}
            />
          ) : (
            <Typography sx={{ fontWeight:500, color: value ? "#0f172a" : "#94a3b8", fontSize: isMobile ? "0.875rem" : "1rem" }}>
              {value || "Not provided"}
            </Typography>
          )}
        </Box>
      </Box>
    );
  };

  // ─── TemplateThumbnail ──────────────────────────────────────────────────────
  const TemplateThumbnail = ({ templateId, themeColor, size = 60 }) => {
    const [err, setErr] = useState(false);
    return (
      <Box sx={{
        width:size, height:size, borderRadius:2, flexShrink:0,
        overflow:"hidden",
        border:`2px solid ${themeColor || BLUE.primary}`,
        bgcolor:"#f5f5f5",
        display:"flex", alignItems:"center", justifyContent:"center",
      }}>
        {!err ? (
          <Box component="img" src={getTemplateThumbnail(templateId)}
            alt={`T${templateId}`} onError={() => setErr(true)}
            sx={{ width:"100%", height:"100%", objectFit:"cover" }} />
        ) : (
          <Box sx={{ width:"100%", height:"100%", display:"flex", flexDirection:"column", p:0.5 }}>
            <Box sx={{ height:"32%", bgcolor: themeColor || BLUE.primary, borderRadius:"4px 4px 0 0", mb:0.3 }} />
            {[0.6,0.4,0.5].map((w,i) => (
              <Box key={i} sx={{ height:3, width:`${w*100}%`, bgcolor:"#d1d5db", borderRadius:1, mb:0.3 }} />
            ))}
            <Typography variant="caption" sx={{ fontSize:"0.5rem", color:"#9ca3af", mt:"auto", textAlign:"center" }}>
              #{templateId}
            </Typography>
          </Box>
        )}
      </Box>
    );
  };

  const ResumeHistoryCard = ({ record }) => {
    const fmt = FORMAT_META[record.fileFormat?.toLowerCase()] || { label: record.fileFormat?.toUpperCase() || "—", bg:"#f0f0f0", color:"#333" };

    return (
      <Card sx={{
        mb:2, borderRadius:2.5,
        boxShadow:"0 1px 6px rgba(0,43,91,0.07)",
        border:`1.5px solid ${BLUE.border}`,
        transition:"box-shadow 0.2s, transform 0.2s",
        "&:hover": { boxShadow:"0 6px 24px rgba(0,43,91,0.14)", transform:"translateY(-1px)" },
        overflow:"visible",
      }}>
        <CardContent sx={{ p: isMobile ? 1.5 : 2.5, pb:`${isMobile ? 12 : 20}px !important` }}>
          <Box sx={{ display:"flex", alignItems:"center", gap:2 }}>

            {/* Thumbnail */}
            <TemplateThumbnail templateId={record.templateId} themeColor={record.themeColor} size={isMobile ? 50 : 62} />

            {/* Middle info */}
            <Box sx={{ flex:1, minWidth:0 }}>
              <Typography fontWeight="bold" sx={{
                fontSize: isMobile ? "0.85rem" : "0.95rem",
                color:"#0f172a",
                whiteSpace:"nowrap", overflow:"hidden", textOverflow:"ellipsis",
                maxWidth: isMobile ? 130 : "100%",
                mb:0.25,
              }}>
                {record.fileName || "Untitled"}
              </Typography>

              {/* Chips row */}
              <Box sx={{ display:"flex", alignItems:"center", gap:0.75, flexWrap:"wrap", mt:0.4 }}>
                <Chip label={fmt.label} size="small" icon={getFormatIcon(record.fileFormat)}
                  sx={{ fontSize:"0.65rem", height:20, fontWeight:700, bgcolor:fmt.bg, color:fmt.color, border:"none",
                    "& .MuiChip-icon": { fontSize:"0.85rem" } }} />
                <Chip label={`Template #${record.templateId}`} size="small"
                  sx={{ fontSize:"0.65rem", height:20, fontWeight:600, bgcolor:BLUE.light, color:BLUE.primary }} />
                {record.themeColor && (
                  <Tooltip title={record.themeColor}>
                    <Box sx={{ width:13, height:13, borderRadius:"50%", bgcolor:record.themeColor,
                      border:"2px solid #fff", boxShadow:"0 0 0 1px #c7d2fe", cursor:"default" }} />
                  </Tooltip>
                )}
              </Box>

              {/* Date / size */}
              <Box sx={{ display:"flex", alignItems:"center", gap:1.5, mt:0.6, flexWrap:"wrap" }}>
                <Typography variant="caption" color="text.secondary" sx={{ fontSize:"0.7rem" }}>
                  📅 {formatDate(record.downloadedAt)}
                </Typography>
                <Typography variant="caption" color="text.secondary" sx={{ fontSize:"0.7rem" }}>
                  💾 {formatFileSize(record.fileSize)}
                </Typography>
              </Box>
            </Box>

            {/* Action buttons — Download, Edit (pencil), Delete */}
            <Box sx={{ display:"flex", flexDirection: isMobile ? "column" : "row", alignItems:"center", gap: isMobile ? 0.5 : 1.25, flexShrink:0 }}>

              {/* Download */}
              <Box sx={{ display:"flex", flexDirection:"column", alignItems:"center", gap:0.3 }}>
                <IconButton size="small" onClick={() => openDownload(record)}
                  sx={{ bgcolor: BLUE.mid, color:"#fff", borderRadius:1.5, p:0.75,
                    "&:hover": { bgcolor: BLUE.primary } }}>
                  <DownloadIcon sx={{ fontSize:"1.05rem" }} />
                </IconButton>
                {!isMobile && <Typography variant="caption" sx={{ fontSize:"0.6rem", color: BLUE.mid, fontWeight:700 }}>Download</Typography>}
              </Box>

              {/* Edit template — pencil icon */}
              <Box sx={{ display:"flex", flexDirection:"column", alignItems:"center", gap:0.3 }}>
                <IconButton size="small" onClick={() => handleEditTemplate(record)}
                  sx={{ bgcolor:"#16a34a", color:"#fff", borderRadius:1.5, p:0.75,
                    "&:hover": { bgcolor:"#15803d" } }}>
                  <EditIcon sx={{ fontSize:"1.05rem" }} />
                </IconButton>
                {!isMobile && <Typography variant="caption" sx={{ fontSize:"0.6rem", color:"#16a34a", fontWeight:700 }}>Edit</Typography>}
              </Box>

              {/* Delete */}
              <Box sx={{ display:"flex", flexDirection:"column", alignItems:"center", gap:0.3 }}>
                <IconButton size="small"
                  onClick={() => setHistoryDeleteConfirm(record._id)}
                  disabled={deletingId === record._id}
                  sx={{ bgcolor:"#dc2626", color:"#fff", borderRadius:1.5, p:0.75,
                    "&:hover": { bgcolor:"#b91c1c" },
                    "&.Mui-disabled": { bgcolor:"#fca5a5", color:"#fff" } }}>
                  {deletingId === record._id
                    ? <CircularProgress size={14} sx={{ color:"#fff" }} />
                    : <DeleteIcon sx={{ fontSize:"1.05rem" }} />}
                </IconButton>
                {!isMobile && <Typography variant="caption" sx={{ fontSize:"0.6rem", color:"#dc2626", fontWeight:700 }}>Delete</Typography>}
              </Box>
            </Box>
          </Box>
        </CardContent>
      </Card>
    );
  };

  // ─── Resume History section ─────────────────────────────────────────────────
  const ResumeHistoryContent = () => (
    <Box>
      <Box sx={{ display:"flex", alignItems:"center", justifyContent:"space-between", mb:3 }}>
        <Box>
          <Typography variant="h6" fontWeight="bold" sx={{ color:"#0f172a" }}>
            Resume Download History
          </Typography>
          {resumeHistory.length > 0 && (
            <Typography variant="body2" color="text.secondary" sx={{ mt:0.25 }}>
              {resumeHistory.length} record{resumeHistory.length !== 1 ? "s" : ""} found
            </Typography>
          )}
        </Box>
        <Button size="small" variant="outlined" onClick={fetchResumeHistory} disabled={historyLoading}
          sx={{ textTransform:"none", fontSize:"0.8rem", borderRadius:2, fontWeight:600,
            borderColor: BLUE.primary, color: BLUE.primary,
            "&:hover": { bgcolor: BLUE.light } }}>
          {historyLoading ? "Loading…" : "↻ Refresh"}
        </Button>
      </Box>
      <Divider sx={{ mb:3, borderColor: BLUE.border }} />

      {historyLoading ? (
        <Box sx={{ display:"flex", justifyContent:"center", py:8 }}>
          <CircularProgress sx={{ color: BLUE.primary }} />
        </Box>
      ) : resumeHistory.length === 0 ? (
        <Box sx={{ textAlign:"center", py:8 }}>
          <HistoryIcon sx={{ fontSize:72, color: BLUE.border, mb:2 }} />
          <Typography variant="h6" color="text.secondary" fontWeight="bold" gutterBottom>
            No Resumes Downloaded Yet
          </Typography>
          <Typography variant="body2" color="text.disabled" sx={{ mb:3 }}>
            Create your first resume and download it — it will appear here automatically.
          </Typography>
          <Button variant="contained" size="large" startIcon={<AddIcon />} onClick={() => navigate("/")}
            sx={{ borderRadius:3, px:4, py:1.5, fontWeight:700, textTransform:"none",
              bgcolor: BLUE.primary, color:"#fff",
              boxShadow:`0 4px 16px rgba(0,43,91,0.3)`,
              "&:hover": { bgcolor: BLUE.primaryDk } }}>
            Create My First Resume
          </Button>
        </Box>
      ) : (
        resumeHistory.map((record) => <ResumeHistoryCard key={record._id} record={record} />)
      )}
    </Box>
  );

  // ─── Shared confirm dialog component ───────────────────────────────────────
  const ConfirmDialog = ({ open, title, body, confirmLabel, confirmColor = "#dc2626", onConfirm, onCancel, loading: busy }) => (
    open ? (
      <Box sx={{ position:"fixed", inset:0, bgcolor:"rgba(0,0,0,0.5)", display:"flex",
        justifyContent:"center", alignItems: isMobile ? "flex-end" : "center", zIndex:9999 }}>
        <Card sx={{
          width: isMobile ? "100%" : 420,
          borderRadius: isMobile ? "24px 24px 0 0" : 3,
          boxShadow:6,
        }}>
          <CardContent sx={{ p:3.5, textAlign:"center", pb:"28px !important" }}>
            {isMobile && <Box sx={{ width:44, height:5, bgcolor: BLUE.border, borderRadius:3, mx:"auto", mb:2.5 }} />}
            <WarningAmberIcon sx={{ fontSize:44, color:confirmColor, mb:1 }} />
            <Typography variant="h6" fontWeight="bold" gutterBottom sx={{ color:"#0f172a" }}>{title}</Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb:3, lineHeight:1.6 }}>{body}</Typography>
            <Box sx={{ display:"flex", gap:1.5, flexDirection: isMobile ? "column" : "row" }}>
              <Button fullWidth variant="outlined" onClick={onCancel}
                sx={{ borderRadius:2.5, py:1.2, textTransform:"none", fontWeight:600 }}>
                Cancel
              </Button>
              <Button fullWidth variant="contained" onClick={onConfirm} disabled={busy}
                startIcon={busy ? <CircularProgress size={16} color="inherit" /> : <DeleteIcon />}
                sx={{ borderRadius:2.5, py:1.2, textTransform:"none", fontWeight:700,
                  bgcolor:confirmColor, "&:hover": { bgcolor:"#b91c1c" } }}>
                {confirmLabel}
              </Button>
            </Box>
          </CardContent>
        </Card>
      </Box>
    ) : null
  );

  // ══════════════════════════════════════════════════════════════════════════
  // MOBILE LAYOUT
  // ══════════════════════════════════════════════════════════════════════════
  if (isMobile) {
    return (
      <Box sx={{ width:"100%", minHeight:"100vh", bgcolor: BLUE.light, pb:9 }}>

        {/* Hero - Mobile */}
        <Box sx={{
          position:"relative",
          background:`linear-gradient(135deg, ${BLUE.primary} 0%, ${BLUE.mid} 100%)`,
          pt:4, pb:5, px:2,
          borderBottomLeftRadius:32, borderBottomRightRadius:32,
          boxShadow:"0 8px 32px rgba(0,43,91,0.25)",
        }}>
          <Box sx={{ display:"flex", flexDirection:"column", alignItems:"center", gap:1.5 }}>
            <Avatar sx={{ width:84, height:84, bgcolor:getAvatarColor(user.name),
              border:"4px solid rgba(255,255,255,0.85)", boxShadow:"0 4px 16px rgba(0,0,0,0.2)",
              fontSize:"2rem", fontWeight:"bold" }}>
              {getInitials(user.name)}
            </Avatar>
            <Typography variant="h6" fontWeight="bold" color="white" sx={{ textAlign:"center" }}>{user.name}</Typography>
            <Box sx={{ display:"flex", alignItems:"center", gap:0.5 }}>
              <EmailIcon sx={{ fontSize:13, color:"rgba(255,255,255,0.8)" }} />
              <Typography variant="caption" color="rgba(255,255,255,0.85)" sx={{ fontSize:"0.72rem" }}>{user.email}</Typography>
            </Box>
            {user.phone && (
              <Box sx={{ display:"flex", alignItems:"center", gap:0.5 }}>
                <PhoneIcon sx={{ fontSize:13, color:"rgba(255,255,255,0.8)" }} />
                <Typography variant="caption" color="rgba(255,255,255,0.85)" sx={{ fontSize:"0.72rem" }}>{user.phone}</Typography>
              </Box>
            )}
            <Box sx={{ display:"flex", gap:1, mt:0.5, flexWrap:"wrap", justifyContent:"center" }}>
              <Button size="small" startIcon={isEditing ? <CloseIcon /> : <EditIcon />} onClick={handleEditToggle}
                variant={isEditing ? "outlined" : "contained"}
                sx={{ borderRadius:3, textTransform:"none", fontWeight:700, fontSize:"0.78rem", px:2,
                  ...(isEditing
                    ? { borderColor:"white", color:"white", bgcolor:"transparent", "&:hover": { bgcolor:"rgba(255,255,255,0.1)" } }
                    : { bgcolor:"rgba(255,255,255,0.92)", color: BLUE.primary, "&:hover": { bgcolor:"white" } }) }}>
                {isEditing ? "Cancel" : "Edit Profile"}
              </Button>
              {isEditing && (
                <Button size="small" variant="contained" startIcon={<SaveIcon />} onClick={handleSaveProfile}
                  sx={{ borderRadius:3, textTransform:"none", fontWeight:700, fontSize:"0.78rem", px:2,
                    bgcolor:"#22c55e", color:"#fff", "&:hover": { bgcolor:"#16a34a" } }}>
                  Save
                </Button>
              )}
            </Box>
            <Button
              startIcon={<LogoutIcon />}
              onClick={handleLogout}
              variant="outlined"
              size="small"
              sx={{
                mt:1,
                color: "rgba(255,255,255,0.85)",
                borderColor: "rgba(255,255,255,0.3)",
                borderRadius: 2,
                textTransform: "none",
                fontWeight: 500,
                px: 2.5,
                py: 0.6,
                "&:hover": {
                  borderColor: "rgba(255,255,255,0.6)",
                  backgroundColor: "rgba(255,255,255,0.1)",
                },
              }}
            >
              Logout
            </Button>
            <Button
              startIcon={<HomeIcon />}
              onClick={() => navigate("/")}
              variant="outlined"
              size="small"
              sx={{
                color: "rgba(255,255,255,0.85)",
                borderColor: "rgba(255,255,255,0.3)",
                borderRadius: 2,
                textTransform: "none",
                fontWeight: 500,
                px: 2.5,
                py: 0.6,
                "&:hover": {
                  borderColor: "rgba(255,255,255,0.6)",
                  backgroundColor: "rgba(255,255,255,0.1)",
                },
              }}
            >
              Back to Home
            </Button>
          </Box>
        </Box>

        {/* Content */}
        <Box sx={{ px:2, pt:3 }}>
          {tabValue === 0 && (
            <Box>
              <Typography variant="subtitle1" fontWeight="bold" sx={{ mb:2, color:"#0f172a" }}>Personal Information</Typography>
              <Card sx={{ borderRadius:3, boxShadow:"0 2px 12px rgba(0,43,91,0.08)" }}>
                <CardContent sx={{ p:2, pb:"16px !important" }}>
                  <InfoField icon={<PersonIcon fontSize="small" />}    label="Full Name"     value={user.name}    field="name" />
                  <InfoField icon={<EmailIcon fontSize="small" />}     label="Email Address" value={user.email}   field="email" />
                  <InfoField icon={<PhoneIcon fontSize="small" />}     label="Phone Number"  value={user.phone}   field="phone" />
                  <InfoField icon={<LocationOnIcon fontSize="small" />} label="Address"      value={user.address} field="address" />
                </CardContent>
              </Card>
            </Box>
          )}
          {tabValue === 1 && <ResumeHistoryContent />}
          {tabValue === 2 && (
            <Box>
              <Typography variant="subtitle1" fontWeight="bold" sx={{ mb:2, color:"#0f172a" }}>Account Settings</Typography>
              <Card sx={{ borderRadius:3, boxShadow:"0 2px 12px rgba(0,43,91,0.08)", border:"1px solid #fecaca" }}>
                <CardContent sx={{ p:2.5, pb:"20px !important" }}>
                  <Typography variant="subtitle2" fontWeight="bold" color="error.main" gutterBottom>⚠️ Danger Zone</Typography>
                  <Typography variant="body2" color="text.secondary" sx={{ mb:2, fontSize:"0.8rem", lineHeight:1.5 }}>
                    Once you delete your account, <b>there is no going back.</b>
                  </Typography>
                  <Button fullWidth variant="contained" startIcon={<DeleteIcon />} onClick={() => setDeleteAccConfirm(true)}
                    sx={{ borderRadius:2.5, py:1.2, fontWeight:"bold", textTransform:"none", bgcolor:"#dc2626", "&:hover": { bgcolor:"#b91c1c" } }}>
                    Delete My Account
                  </Button>
                </CardContent>
              </Card>
            </Box>
          )}
        </Box>

        {/* Bottom nav */}
        <Paper elevation={8} sx={{ position:"fixed", bottom:0, left:0, right:0, zIndex:1300,
          borderTopLeftRadius:20, borderTopRightRadius:20, overflow:"hidden",
          boxShadow:"0 -4px 24px rgba(0,43,91,0.12)" }}>
          <BottomNavigation value={tabValue} onChange={(_, v) => setTabValue(v)}
            sx={{ height:66, "& .MuiBottomNavigationAction-root": { minWidth:"auto", "&.Mui-selected": { color: BLUE.primary } },
              "& .MuiBottomNavigationAction-label": { fontSize:"0.7rem", fontWeight:600 } }}>
            <BottomNavigationAction label="Account" icon={<PersonIcon />} />
            <BottomNavigationAction label="History"  icon={<HistoryIcon />} />
            <BottomNavigationAction label="Settings" icon={<SettingsIcon />} />
          </BottomNavigation>
        </Paper>

        {/* Download mobile bottom sheet */}
        {downloadRecord && (
          <Box sx={{ position:"fixed", inset:0, bgcolor:"rgba(0,0,0,0.55)", display:"flex", justifyContent:"center", alignItems:"flex-end", zIndex:9999 }}>
            <Card sx={{ width:"100%", borderTopLeftRadius:24, borderTopRightRadius:24, borderBottomLeftRadius:0, borderBottomRightRadius:0 }}>
              <CardContent sx={{ p:3, pb:"32px !important" }}>
                <Box sx={{ width:44, height:5, bgcolor: BLUE.border, borderRadius:3, mx:"auto", mb:2.5 }} />
                <Typography variant="h6" fontWeight="bold" gutterBottom>Download Resume</Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mb:2 }}>
                  Opens editor with <b>Template #{downloadRecord.templateId}</b>.
                </Typography>
                <Typography variant="body2" fontWeight={700} sx={{ mb:1 }}>Choose format:</Typography>
                <RadioGroup value={downloadFormat} onChange={(e) => setDownloadFormat(e.target.value)} sx={{ mb:2 }}>
                  {["pdf","png","docx","doc"].map((f) => (
                    <FormControlLabel key={f} value={f} control={<Radio size="small" />} label={f.toUpperCase()} />
                  ))}
                </RadioGroup>
                <Box sx={{ display:"flex", gap:1.5, flexDirection:"column" }}>
                  <Button fullWidth variant="contained" onClick={handleReDownload} startIcon={<DownloadIcon />}
                    sx={{ borderRadius:2.5, py:1.2, textTransform:"none", fontWeight:700, bgcolor: BLUE.primary, "&:hover": { bgcolor: BLUE.primaryDk } }}>
                    Open in Editor
                  </Button>
                  <Button fullWidth variant="outlined" onClick={() => setDownloadRecord(null)}
                    sx={{ borderRadius:2.5, py:1.2, textTransform:"none", fontWeight:600 }}>Cancel</Button>
                </Box>
              </CardContent>
            </Card>
          </Box>
        )}

        <ConfirmDialog
          open={!!historyDeleteConfirm}
          title="Delete Record"
          body="Are you sure you want to delete this resume history record? This cannot be undone."
          confirmLabel="Yes, Delete"
          onConfirm={() => handleDeleteHistory(historyDeleteConfirm)}
          onCancel={() => setHistoryDeleteConfirm(null)}
        />
        <ConfirmDialog
          open={deleteAccConfirm}
          title="Delete Account"
          body="All your data will be permanently deleted. This action cannot be reversed."
          confirmLabel="Delete My Account"
          onConfirm={handleDeleteAccount}
          onCancel={() => setDeleteAccConfirm(false)}
        />

        <Snackbar open={snackbar.open} autoHideDuration={4000} onClose={() => setSnackbar((s) => ({ ...s, open:false }))}
          anchorOrigin={{ vertical:"top", horizontal:"center" }}>
          <Alert severity={snackbar.severity} sx={{ width:"100%" }} onClose={() => setSnackbar((s) => ({ ...s, open:false }))}>
            {snackbar.message}
          </Alert>
        </Snackbar>
      </Box>
    );
  }

  // ══════════════════════════════════════════════════════════════════════════
  // DESKTOP LAYOUT — Sidebar only, no header
  // ══════════════════════════════════════════════════════════════════════════
  return (
    <Box sx={{ width:"100vw", minHeight:"100vh", display:"flex",
      bgcolor: BLUE.light, m:0, p:0, boxSizing:"border-box", overflow:"hidden" }}>

      {/* ── SIDEBAR ──────────────────────────────────────────────────────── */}
      <Box sx={{
        width:280, flexShrink:0, bgcolor: BLUE.navBg,
        display:"flex", flexDirection:"column",
        boxShadow:"2px 0 12px rgba(0,0,0,0.2)",
        height:"100vh",
        position:"sticky",
        top:0,
      }}>
        {/* Avatar - At Top */}
        <Box sx={{ px:3, py:3.5, display:"flex", flexDirection:"column", alignItems:"center",
          borderBottom:"1px solid rgba(255,255,255,0.07)" }}>
          <Avatar sx={{
            width:80, height:80,
            bgcolor:getAvatarColor(user.name),
            fontSize:"2rem", fontWeight:"bold",
            border:`3px solid ${BLUE.mid}`,
            boxShadow:"0 4px 16px rgba(0,0,0,0.3)",
            mb:1.5,
          }}>
            {getInitials(user.name)}
          </Avatar>
          <Typography variant="h6" fontWeight="bold" sx={{ color:"#f1f5f9", textAlign:"center", lineHeight:1.3 }}>
            {user.name}
          </Typography>
          <Typography variant="caption" sx={{ color:"rgba(255,255,255,0.5)", textAlign:"center", mt:0.25, fontSize:"0.72rem" }}>
            {user.email}
          </Typography>
          {user.phone && (
            <Typography variant="caption" sx={{ color:"rgba(255,255,255,0.4)", textAlign:"center", mt:0.2, fontSize:"0.7rem" }}>
              {user.phone}
            </Typography>
          )}
        </Box>

        {/* Edit / Save */}
        <Box sx={{ px:2.5, py:2, borderBottom:"1px solid rgba(255,255,255,0.07)" }}>
          <Button fullWidth startIcon={isEditing ? <CloseIcon /> : <EditIcon />}
            variant={isEditing ? "outlined" : "contained"} onClick={handleEditToggle}
            sx={{ borderRadius:2, textTransform:"none", fontWeight:600, mb:1, fontSize:"0.85rem",
              ...(isEditing
                ? { borderColor:"rgba(255,255,255,0.35)", color:"rgba(255,255,255,0.8)",
                    "&:hover": { bgcolor:"rgba(255,255,255,0.08)", borderColor:"#fff" } }
                : { bgcolor: BLUE.primary, color:"#fff", "&:hover": { bgcolor: BLUE.mid } }) }}>
            {isEditing ? "Cancel Edit" : "Edit Profile"}
          </Button>
          {isEditing && (
            <Button fullWidth variant="contained" startIcon={<SaveIcon />} onClick={handleSaveProfile}
              sx={{ borderRadius:2, textTransform:"none", fontWeight:700, bgcolor:"#22c55e",
                color:"#fff", fontSize:"0.85rem", "&:hover": { bgcolor:"#16a34a" } }}>
              Save Changes
            </Button>
          )}
        </Box>

        {/* Nav items */}
        <Box sx={{ flex:1, pt:0.5, overflowY:"auto" }}>
          {[
            { icon:<AccountCircleIcon sx={{ fontSize:"1.2rem" }} />, label:"Account Info",   idx:0 },
            { icon:<DescriptionIcon   sx={{ fontSize:"1.2rem" }} />, label:"Resume History", idx:1 },
            { icon:<SettingsIcon      sx={{ fontSize:"1.2rem" }} />, label:"Settings",       idx:2 },
          ].map(({ icon, label, idx }) => (
            <Box key={idx} onClick={() => setTabValue(idx)} sx={{
              display:"flex", alignItems:"center", gap:1.5,
              px:2.5, py:1.6,
              cursor:"pointer",
              borderLeft: tabValue===idx ? `4px solid ${BLUE.mid}` : "4px solid transparent",
              bgcolor:     tabValue===idx ? BLUE.navActive : "transparent",
              color:       tabValue===idx ? "#fff" : "rgba(255,255,255,0.5)",
              transition:"all 0.16s",
              "&:hover": { bgcolor: tabValue===idx ? BLUE.navActive : BLUE.navHover, color:"rgba(255,255,255,0.9)" },
            }}>
              <Box sx={{ color:"inherit", display:"flex" }}>{icon}</Box>
              <Typography variant="body2" fontWeight={tabValue===idx ? 700 : 500} sx={{ color:"inherit", fontSize:"0.88rem" }}>
                {label}
              </Typography>
              {idx===1 && resumeHistory.length > 0 && (
                <Box sx={{ ml:"auto", bgcolor: BLUE.mid, color:"#fff", borderRadius:"10px",
                  px:0.8, py:0.1, fontSize:"0.65rem", fontWeight:700, lineHeight:1.6 }}>
                  {resumeHistory.length}
                </Box>
              )}
            </Box>
          ))}
        </Box>

        {/* ── BOTTOM BUTTONS ──────────────────────────────────────────── */}
        <Box sx={{ px:2.5, py:2, borderTop:"1px solid rgba(255,255,255,0.07)" }}>
          <Button
            fullWidth
            startIcon={<HomeIcon />}
            onClick={() => navigate("/")}
            variant="outlined"
            sx={{
              borderRadius:2,
              textTransform:"none",
              fontWeight:600,
              borderColor:"rgba(255,255,255,0.25)",
              color:"rgba(255,255,255,0.7)",
              fontSize:"0.85rem",
              mb:1,
              "&:hover": {
                borderColor:"rgba(255,255,255,0.5)",
                color:"#fff",
                backgroundColor:"rgba(255,255,255,0.08)",
              },
            }}
          >
            Back to Home
          </Button>
          <Button
            fullWidth
            startIcon={<LogoutIcon />}
            onClick={handleLogout}
            variant="outlined"
            sx={{
              borderRadius:2,
              textTransform:"none",
              fontWeight:600,
              borderColor:"rgba(255,255,255,0.25)",
              color:"rgba(255,255,255,0.7)",
              fontSize:"0.85rem",
              "&:hover": {
                borderColor:"rgba(255,255,255,0.5)",
                color:"#fff",
                backgroundColor:"rgba(255,255,255,0.08)",
              },
            }}
          >
            Logout
          </Button>
        </Box>
      </Box>

      {/* ── MAIN CONTENT ─────────────────────────────────────────────────── */}
      <Box sx={{ flex:1, overflow:"auto", p:4, bgcolor: BLUE.light }}>

        {/* Account Info */}
        {tabValue === 0 && (
          <Card sx={{ borderRadius:2.5, boxShadow:"0 1px 8px rgba(0,43,91,0.08)", border:`1.5px solid ${BLUE.border}` }}>
            <CardContent sx={{ p:4 }}>
              <Box sx={{ display:"flex", alignItems:"center", gap:1.5, mb:2.5 }}>
                <Box sx={{ width:4, height:26, borderRadius:2, bgcolor: BLUE.primary }} />
                <Typography variant="h6" fontWeight="bold" sx={{ color:"#0f172a" }}>Personal Information</Typography>
              </Box>
              <Divider sx={{ mb:3, borderColor: BLUE.border }} />
              <InfoField icon={<PersonIcon />}    label="Full Name"     value={user.name}    field="name" />
              <InfoField icon={<EmailIcon />}     label="Email Address" value={user.email}   field="email" />
              <InfoField icon={<PhoneIcon />}     label="Phone Number"  value={user.phone}   field="phone" />
              <InfoField icon={<LocationOnIcon />} label="Address"      value={user.address} field="address" />
            </CardContent>
          </Card>
        )}

        {/* Resume History */}
        {tabValue === 1 && (
          <Card sx={{ borderRadius:2.5, boxShadow:"0 1px 8px rgba(0,43,91,0.08)", border:`1.5px solid ${BLUE.border}` }}>
            <CardContent sx={{ p:4 }}>
              <ResumeHistoryContent />
            </CardContent>
          </Card>
        )}

        {/* Settings */}
        {tabValue === 2 && (
          <Card sx={{ borderRadius:2.5, boxShadow:"0 1px 8px rgba(0,43,91,0.08)", border:`1.5px solid ${BLUE.border}` }}>
            <CardContent sx={{ p:4 }}>
              <Box sx={{ display:"flex", alignItems:"center", gap:1.5, mb:2.5 }}>
                <Box sx={{ width:4, height:26, borderRadius:2, bgcolor:"#dc2626" }} />
                <Typography variant="h6" fontWeight="bold" sx={{ color:"#0f172a" }}>Account Settings</Typography>
              </Box>
              <Divider sx={{ mb:3, borderColor: BLUE.border }} />
              <Typography variant="subtitle1" fontWeight="bold" color="error.main" gutterBottom>⚠️ Danger Zone</Typography>
              <Paper sx={{ p:3, bgcolor:alpha(theme.palette.error.light,0.06),
                border:`1px solid ${alpha(theme.palette.error.main,0.22)}`, borderRadius:2 }}>
                <Typography variant="body2" color="text.secondary" sx={{ mb:2 }}>
                  Once you delete your account, <b>there is no going back.</b> All data will be permanently removed.
                </Typography>
                <Button variant="contained" startIcon={<DeleteIcon />} onClick={() => setDeleteAccConfirm(true)}
                  sx={{ borderRadius:2, px:3, py:1, fontWeight:600, textTransform:"none",
                    bgcolor:"#dc2626", "&:hover": { bgcolor:"#b91c1c" } }}>
                  Delete My Account
                </Button>
              </Paper>
            </CardContent>
          </Card>
        )}
      </Box>

      {/* ── DESKTOP DIALOGS ────────────────────────────────────────────────── */}

      {/* Download */}
      <Dialog open={!!downloadRecord} onClose={() => setDownloadRecord(null)} fullWidth maxWidth="sm" PaperProps={{ sx:{ borderRadius:3 } }}>
        <DialogTitle sx={{ fontWeight:"bold", color:"#0f172a" }}>Download Resume</DialogTitle>
        <DialogContent>
          {downloadRecord && (
            <Box>
              <Typography variant="body2" color="text.secondary" sx={{ mb:2 }}>
                Opens the editor with <b>Template #{downloadRecord.templateId}</b> pre-loaded.
              </Typography>
              <Typography variant="body2" fontWeight={700} sx={{ mb:1 }}>Choose format:</Typography>
              <RadioGroup value={downloadFormat} onChange={(e) => setDownloadFormat(e.target.value)}>
                {[["pdf","PDF (.pdf)"],["png","PNG (.png)"],["docx","Word (.docx)"],["doc","Word (.doc)"]].map(([v,l]) => (
                  <FormControlLabel key={v} value={v} control={<Radio />} label={l} />
                ))}
              </RadioGroup>
            </Box>
          )}
        </DialogContent>
        <DialogActions sx={{ px:3, pb:3, gap:1 }}>
          <Button onClick={() => setDownloadRecord(null)} variant="outlined"
            sx={{ textTransform:"none", borderRadius:2, flex:1 }}>Cancel</Button>
          <Button onClick={handleReDownload} variant="contained" startIcon={<DownloadIcon />}
            sx={{ textTransform:"none", borderRadius:2, flex:1, fontWeight:600,
              bgcolor: BLUE.primary, "&:hover": { bgcolor: BLUE.primaryDk } }}>
            Open in Editor
          </Button>
        </DialogActions>
      </Dialog>

      {/* Confirm: delete history record */}
      <ConfirmDialog
        open={!!historyDeleteConfirm}
        title="Delete History Record"
        body="Are you sure you want to delete this resume history record? This action cannot be undone."
        confirmLabel="Delete Record"
        onConfirm={() => handleDeleteHistory(historyDeleteConfirm)}
        onCancel={() => setHistoryDeleteConfirm(null)}
      />

      {/* Confirm: delete account */}
      <ConfirmDialog
        open={deleteAccConfirm}
        title="Delete Account"
        body="All your data will be permanently removed. This action cannot be reversed."
        confirmLabel="Delete My Account"
        onConfirm={handleDeleteAccount}
        onCancel={() => setDeleteAccConfirm(false)}
      />

      <Snackbar open={snackbar.open} autoHideDuration={4000}
        onClose={() => setSnackbar((s) => ({ ...s, open:false }))}
        anchorOrigin={{ vertical:"bottom", horizontal:"center" }}>
        <Alert severity={snackbar.severity} sx={{ width:"100%" }}
          onClose={() => setSnackbar((s) => ({ ...s, open:false }))}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default MyAccountPage;